import type { AdminAppOverview, AdminClientOverview } from "../types/admin";
import type { AdminClientCreatedRange } from "./admin-client-created-range";
import {
  EMPTY_ADMIN_CLIENT_CREATED_RANGE,
  matchesAdminClientCreatedRange,
} from "./admin-client-created-range";

export type AdminOverviewPlan =
  "basic" | "advanced" | "professional" | "no-plan" | "unknown-plan";
export type AdminOverviewStatus = "trial" | "active" | "expired";
export type AdminOverviewHealth = "reachable" | "unreachable" | "unchecked";
export type AdminOverviewAppPresence = "all" | "with-apps" | "without-apps";

/** Empty multi-selects are unrestricted; populated dimensions combine with AND. */
export interface AdminOverviewFilters {
  query: string;
  plans: readonly AdminOverviewPlan[];
  statuses: readonly AdminOverviewStatus[];
  health: readonly AdminOverviewHealth[];
  appPresence: AdminOverviewAppPresence;
  createdRange: AdminClientCreatedRange;
}

/** Fresh arrays and range on every call, suitable for independent toolbar state. */
export function createDefaultAdminOverviewFilters(): AdminOverviewFilters {
  return {
    query: "",
    plans: [],
    statuses: [],
    health: [],
    appPresence: "all",
    createdRange: { ...EMPTY_ADMIN_CLIENT_CREATED_RANGE },
  };
}

export interface FilteredAdminClientOverview {
  /** Original payload object, including unfiltered counts, users and apps. */
  client: AdminClientOverview;
  /** Original app objects that satisfy all app-level criteria, in payload order. */
  visibleApps: AdminAppOverview[];
}

function contains(value: unknown, query: string): boolean {
  return typeof value === "string" && value.toLowerCase().includes(query);
}

function matchesPerson(
  person: { firstName: string | null; lastName: string | null; email: string },
  query: string
): boolean {
  const fullName = [person.firstName, person.lastName]
    .filter((name): name is string => typeof name === "string" && !!name.trim())
    .map((name) => name.trim())
    .join(" ");
  return (
    contains(person.firstName, query) ||
    contains(person.lastName, query) ||
    contains(fullName, query) ||
    contains(person.email, query)
  );
}

function planOf(client: AdminClientOverview): AdminOverviewPlan {
  const key = client.subscription?.planKey ?? client.limits?.planKey;
  if (key == null || key === "") return "no-plan";
  if (key === "basic" || key === "advanced" || key === "professional") {
    return key;
  }
  return "unknown-plan";
}

function statusOf(app: AdminAppOverview): AdminOverviewStatus | undefined {
  switch (app.activationStatus) {
    case "trial":
      return "trial";
    case "active_included":
    case "active_paid":
      return "active";
    case "expired":
      return "expired";
    default:
      return undefined;
  }
}

function healthOf(app: AdminAppOverview): AdminOverviewHealth | undefined {
  switch (app.health) {
    case "ok":
      return "reachable";
    case "unreachable":
      return "unreachable";
    case "unknown":
      return "unchecked";
    default:
      return undefined;
  }
}

/**
 * Pure selector. Parent search hits make every app eligible for search, but
 * never bypass status/health. App search, status and health must match the SAME
 * app. Presence uses the original app list, not the filtered list or appCount.
 * Invalid date ranges are unrestricted (the toolbar owns last-valid state).
 */
export function filterAdminOverview(
  clients: readonly AdminClientOverview[],
  filters: AdminOverviewFilters
): FilteredAdminClientOverview[] {
  const query = filters.query.trim().toLowerCase();
  const appFilterActive =
    filters.statuses.length > 0 || filters.health.length > 0;
  const result: FilteredAdminClientOverview[] = [];

  for (const client of clients) {
    const apps = client.apps ?? [];
    if (
      (filters.plans.length > 0 && !filters.plans.includes(planOf(client))) ||
      !matchesAdminClientCreatedRange(client.createdAt, filters.createdRange) ||
      (filters.appPresence === "with-apps" && apps.length === 0) ||
      (filters.appPresence === "without-apps" && apps.length > 0)
    ) {
      continue;
    }

    const parentMatches =
      !query ||
      contains(client.name, query) ||
      contains(client.companyName, query) ||
      contains(client.contactEmail, query) ||
      (client.users ?? []).some((user) => user && matchesPerson(user, query)) ||
      (client.invitations ?? []).some(
        (invitation) => invitation && matchesPerson(invitation, query)
      );

    const visibleApps = apps.filter((app) => {
      const status = statusOf(app);
      const health = healthOf(app);
      return (
        (filters.statuses.length === 0 ||
          (status !== undefined && filters.statuses.includes(status))) &&
        (filters.health.length === 0 ||
          (health !== undefined && filters.health.includes(health))) &&
        (parentMatches ||
          contains(app.name, query) ||
          contains(app.url, query) ||
          (app.accessUsers ?? []).some(
            (user) => user && matchesPerson(user, query)
          ))
      );
    });

    if (visibleApps.length > 0 || (!appFilterActive && parentMatches)) {
      result.push({ client, visibleApps });
    }
  }
  return result;
}
